import { bootstrapApplication } from '@angular/platform-browser';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import * as Sentry from '@sentry/angular';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { ClientObservabilityService, normalizeTelemetryRoute } from './app/shared/observability/client-observability.service';
import { runtimeConfig } from './app/shared/runtime-config';

const bootstrapStartedAt = performance.now();

Sentry.init({
  dsn: runtimeConfig.sentryDsn,
  enabled: !!runtimeConfig.sentryDsn,
  environment: runtimeConfig.environmentName,
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  tracesSampleRate: 1.0,
  release: runtimeConfig.release,
  // Session Replay records the full page URL in rrweb metadata before the SDK's
  // error/transaction sanitizers run. OIDC query parameters make it unsafe here.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  sendDefaultPii: false,
  beforeSend(event) {
    if (event.request) {
      if (event.request.url) event.request.url = stripUrl(event.request.url);
      delete event.request.data;
      delete event.request.cookies;
      delete event.request.query_string;
      delete event.request.env;
      if (event.request.headers) {
        for (const key of Object.keys(event.request.headers)) {
          if (/authorization|cookie|token|code|phone/i.test(key)) {
            delete event.request.headers[key];
          }
        }
      }
    }
    if (event.message) event.message = scrubSensitiveText(event.message);
    if (event.transaction) event.transaction = stripUrl(event.transaction);
    for (const value of event.exception?.values ?? []) {
      if (value.value) value.value = scrubSensitiveText(value.value);
      for (const frame of value.stacktrace?.frames ?? []) {
        if (frame.filename) frame.filename = stripUrl(frame.filename);
      }
    }
    // Application/library extras are unbounded and may contain an entire HTTP body.
    // Keep diagnostics in explicit tags/contexts instead of attempting a fragile denylist.
    delete event.extra;
    if (event.contexts) event.contexts = sanitizeSentryValue(event.contexts);
    if (event.tags) event.tags = sanitizeSentryValue(event.tags);
    if (event.user?.id) event.user = { id: scrubSensitiveText(String(event.user.id)) };
    else delete event.user;
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => sanitizeBreadcrumb(breadcrumb));
    }
    return event;
  },
  beforeSendTransaction(event) {
    if (event.transaction) event.transaction = stripUrl(event.transaction);
    if (event.request?.url) event.request.url = stripUrl(event.request.url);
    if (event.request) {
      delete event.request.query_string;
      delete event.request.data;
      delete event.request.cookies;
      delete event.request.headers;
    }
    if (event.contexts) event.contexts = sanitizeSentryValue(event.contexts);
    if (event.tags) event.tags = sanitizeSentryValue(event.tags);
    for (const span of event.spans ?? []) sanitizeSentrySpan(span);
    return event;
  },
  beforeSendSpan(span) {
    return sanitizeSentrySpan(span);
  },
  beforeBreadcrumb(breadcrumb) {
    return sanitizeBreadcrumb(breadcrumb);
  },
});

bootstrapApplication(AppComponent, appConfig)
  .then((appRef) => {
    const telemetry = appRef.injector.get(ClientObservabilityService);
    Sentry.setTags({
      auth_flow_id: telemetry.flowId,
      client_app: runtimeConfig.clientApp,
    });
    telemetry.report({
      name: 'angular.bootstrapped',
      outcome: 'success',
      duration_ms: elapsed(bootstrapStartedAt),
    });
    observeRouter(appRef.injector.get(Router), telemetry);
    telemetry.capturePerformance();
  })
  .catch((error: unknown) => {
    window.dispatchEvent(new CustomEvent('app:failed', { detail: { stage: 'angular_bootstrap' } }));
    Sentry.captureException(error);
    console.error(error);
  });

function observeRouter(router: Router, telemetry: ClientObservabilityService): void {
  const started = new Map<number, number>();
  let startupCompleted = false;

  const rendered = (route: string, durationMs?: number): void => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      telemetry.report({
        name: 'route.rendered',
        outcome: 'success',
        route: telemetry.safeRoute(route),
        ...(durationMs === undefined ? {} : { duration_ms: durationMs }),
      });
      if (!startupCompleted) {
        startupCompleted = true;
        telemetry.report({
          name: 'startup.completed',
          outcome: 'success',
          duration_ms: elapsed(bootstrapStartedAt),
          route: telemetry.safeRoute(route),
          details: { state: window.__AUTH_UI_STARTUP_STALLED__ ? 'recovered' : 'ready' },
        });
        window.dispatchEvent(new Event('app:ready'));
      }
    }));
  };

  router.events.subscribe((event) => {
    if (event instanceof NavigationStart) {
      started.set(event.id, performance.now());
      telemetry.report({
        name: 'route.started',
        route: telemetry.safeRoute(event.url),
        details: { navigation_type: event.navigationTrigger ?? 'imperative' },
      });
      return;
    }
    if (event instanceof NavigationEnd) {
      const startedAt = started.get(event.id);
      started.delete(event.id);
      rendered(event.urlAfterRedirects, startedAt === undefined ? undefined : elapsed(startedAt));
      return;
    }
    if (event instanceof NavigationCancel) {
      const startedAt = started.get(event.id);
      started.delete(event.id);
      telemetry.report({
        name: 'route.failed',
        outcome: 'cancelled',
        error_type: 'navigation_cancel',
        route: telemetry.safeRoute(event.url),
        ...(startedAt === undefined ? {} : { duration_ms: elapsed(startedAt) }),
        details: { reason: `code_${event.code}` },
      });
      return;
    }
    if (event instanceof NavigationError) {
      const startedAt = started.get(event.id);
      started.delete(event.id);
      telemetry.report({
        name: 'route.failed',
        outcome: 'error',
        error_type: 'navigation_error',
        route: telemetry.safeRoute(event.url),
        ...(startedAt === undefined ? {} : { duration_ms: elapsed(startedAt) }),
        details: { reason: 'router_error' },
      });
    }
  });

  // bootstrapApplication can resolve after a synchronous initial navigation. Record
  // its visible result explicitly; subsequent navigations are handled above.
  if (router.navigated) {
    telemetry.report({
      name: 'route.started',
      route: telemetry.safeRoute(router.url),
      details: { navigation_type: 'initial' },
    });
    rendered(router.url);
  }
}

function elapsed(startedAt: number): number {
  return Math.max(0, Math.round(performance.now() - startedAt));
}

function stripUrl(value: string): string {
  const operation = value.match(/^([A-Z]{2,10})\s+(.+)$/);
  if (operation) return `${operation[1]} ${stripUrl(operation[2])}`;
  try {
    const url = new URL(value, window.location.origin);
    const path = normalizeTelemetryRoute(url.pathname, window.location.origin);
    return value.startsWith('/') ? path : `${url.origin}${path}`;
  } catch {
		return scrubSensitiveText(value.split(/[?#]/, 1)[0] ?? value);
  }
}

function scrubSensitiveText(value: string): string {
  return value
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [Filtered]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[JWT]')
    .replace(
      /([?&](?:phone|session_id|code|state|nonce|redirect_uri|code_challenge|code_verifier|access_token|refresh_token|id_token)=)[^&#\s]*/gi,
      '$1[Filtered]',
    )
    .replace(/((?:phone|otp|password|client_secret|code_verifier)\s*[:=]\s*)[^\s,;]+/gi, '$1[Filtered]')
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[EMAIL]')
    .replace(/(^|[^A-Za-z0-9])(?:\+?\d[\s().-]*){7,15}(?![A-Za-z0-9])/g, '$1[PHONE]');
}

function sanitizeSentrySpan<T extends { description?: string; data: Record<string, unknown> }>(span: T): T {
  if (span.description) span.description = stripUrl(scrubSensitiveText(span.description));
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(span.data ?? {})) {
    if (/(^|\.)(query|query_string|request\.body|response\.body|http\.request\.body|http\.response\.body|authorization|cookie)(\.|$)/i.test(key)) {
      continue;
    }
    if (typeof value === 'string' && /(^|\.)(url|target|route|path|description|name)(\.|$)/i.test(key)) {
      sanitized[key] = stripUrl(scrubSensitiveText(value));
    } else {
      sanitized[key] = sanitizeSentryValue(value);
    }
  }
  span.data = sanitized;
  return span;
}

function sanitizeBreadcrumb<T extends { message?: string; data?: Record<string, unknown> }>(breadcrumb: T): T {
  if (breadcrumb.message) breadcrumb.message = scrubSensitiveText(breadcrumb.message);
  if (breadcrumb.data) {
    breadcrumb.data = sanitizeSentryValue(breadcrumb.data);
    for (const key of ['url', 'from', 'to']) {
      const value = breadcrumb.data[key];
      if (typeof value === 'string') breadcrumb.data[key] = stripUrl(value);
    }
  }
  return breadcrumb;
}

function sanitizeSentryValue<T>(value: T, seen = new WeakSet<object>()): T {
  if (typeof value === 'string') return scrubSensitiveText(value) as T;
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return '[Circular]' as T;
  seen.add(value);
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeSentryValue(item, seen)) as T;
  }
  const source = value as Record<string, unknown>;
  const sanitized: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(source)) {
    if (/(^|_)(authorization|cookie|password|phone|otp|access_token|refresh_token|id_token|client_secret|code_verifier|request_body|response_body|form_data)(_|$)/i.test(key)) {
      sanitized[key] = '[Filtered]';
    } else if (typeof item === 'string' && /(^|_)(url|route|path)(_|$)/i.test(key)) {
      sanitized[key] = stripUrl(item);
    } else {
      sanitized[key] = sanitizeSentryValue(item, seen);
    }
  }
  return sanitized as T;
}
